import axios from "axios";
import Cookies from "js-cookie";

let showSessionExpiredModal = null;
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      // Do not reject the promise. We hang it so the components don't get the error and don't fire toasts
      // prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const https = axios.create({
  baseURL: process.env.REACT_APP_PUBLIC_BACKEND_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Instance riêng cho refresh token (không qua interceptor)
export const refreshAxios = axios.create({
  baseURL: process.env.REACT_APP_PUBLIC_BACKEND_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Helper: Kiểm tra token có hết hạn không
const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const currentTime = Math.floor(Date.now() / 1000);

    // Refresh trước 10 giây trước khi hết hạn
    const bufferTime = 10; // seconds
    const timeLeft = payload.exp - currentTime;

    // QUAN TRỌNG: Chỉ return true nếu thực sự sắp hết hạn
    // Không check nếu token còn hơn 10s
    return timeLeft < bufferTime;
  } catch (error) {
    console.error("Error parsing token:", error);
    return true;
  }
};

// Helper: Refresh token
const refreshTokenIfNeeded = async () => {
  const currentToken = Cookies.get("token_client");
  const refreshToken = Cookies.get("refresh_token_client");

  // QUAN TRỌNG: Nếu không có token hiện tại, không làm gì cả
  if (!currentToken) {
    return null;
  }

  // Nếu token chưa hết hạn, không cần refresh
  if (!isTokenExpired(currentToken)) {
    return currentToken;
  }

  console.log("⏰ Token sắp hết hạn, đang refresh...");

  // Nếu không có refresh token
  if (!refreshToken) {
    console.log("❌ No refresh token found");
    if (typeof showSessionExpiredModal === "function") {
      showSessionExpiredModal();
    }
    // Return a hanging promise to prevent caller components from firing multiple error toasts
    return new Promise(() => {});
  }

  // Nếu đang refresh, đợi kết quả
  if (isRefreshing) {
    console.log("⏳ Already refreshing, waiting...");
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;

  try {
    console.log("🔑 Calling refresh token API...");

    const response = await refreshAxios.post("/refresh/client", {
      refreshToken,
    });

    console.log("✅ Refresh token successful");

    if (response.data && response.data.token) {
      const newToken = response.data.token;
      const newRefreshToken = response.data.refreshToken || refreshToken;

      // Cập nhật cookies
      Cookies.set("token_client", newToken, { expires: 7 });
      if (newRefreshToken) {
        Cookies.set("refresh_token_client", newRefreshToken, { expires: 14 });
      }

      console.log("💾 Updated tokens in cookies");

      // Process queue
      processQueue(null, newToken);
      isRefreshing = false;

      return newToken;
    } else {
      throw new Error("Invalid refresh token response");
    }
  } catch (error) {
    console.error("❌ Refresh token failed:", error);
    processQueue(error, null);
    isRefreshing = false;

    // Refresh token thất bại -> xóa cookies
    Cookies.remove("token_client");
    Cookies.remove("refresh_token_client");
    Cookies.remove("user_client");

    if (typeof showSessionExpiredModal === "function") {
      showSessionExpiredModal();
    }

    // Return a hanging promise to stop caller components from executing their catch block
    return new Promise(() => {});
  }
};

// Request interceptor - Kiểm tra và refresh token trước khi gửi request
https.interceptors.request.use(
  async (config) => {
    let newConfig = { ...config };

    // QUAN TRỌNG: Bỏ qua check cho các endpoint authentication
    const skipRefreshUrls = [
      "/refresh/client",
      "/login/client",
      "/register",
      "/forgot-password",
      "/verify-code",
      "/reset-password",
      "/login/client",
      "/register",
    ];

    const shouldSkip = skipRefreshUrls.some((url) => config.url?.includes(url));

    if (shouldSkip) {
      // Chỉ thêm token nếu có, không check expired
      const token = Cookies.get("token_client");
      if (token) {
        newConfig.headers.Authorization = `Bearer ${token}`;
      }
      return newConfig;
    }

    try {
      // Kiểm tra và refresh token nếu cần
      const token = await refreshTokenIfNeeded();

      if (token) {
        newConfig.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error("Error refreshing token:", error);
      // Vẫn dùng token cũ nếu refresh fail
      const currentToken = Cookies.get("token_client");
      if (currentToken) {
        newConfig.headers.Authorization = `Bearer ${currentToken}`;
      }
      
      // If token refresh fails, it's safer to just return a hanging promise so the request doesn't dispatch with an expired token
      return new Promise(() => {});
    }

    if (newConfig.data instanceof FormData) {
      newConfig.headers["Content-Type"] = "multipart/form-data";
    }

    return newConfig;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor - Xử lý lỗi 401/403 (backup)
https.interceptors.response.use(
  (response) => {
    if (response?.config?.responseType === "blob") {
      return response;
    }
    if (response && response.data) return response.data;
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    console.log("❌ Request failed:", {
      url: originalRequest?.url,
      status: error.response?.status,
      message: error.response?.data?.message,
    });

    // Xử lý lỗi token (backup case)
    const isTokenError =
      error.response &&
      (error.response.status === 401 ||
        (error.response.status === 403 &&
          error.response.data?.message === "Invalid or expired token"));

    if (
      isTokenError &&
      !originalRequest.url?.includes("/refresh/client")
    ) {
      console.log("🔄 Token error detected");

      if (!originalRequest._retry) {
        console.log("🔄 Attempting refresh (backup)...");
        originalRequest._retry = true;

        try {
          const newToken = await refreshTokenIfNeeded();

          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return https(originalRequest);
          }
        } catch (refreshError) {
          console.error("❌ Backup refresh failed:", refreshError);
          // Return a hanging promise so components do not trigger toast multiple errors
          return new Promise(() => {});
        }
      } else {
        // Repeated token error after retry, also block the request from returning to components
        return new Promise(() => {});
      }
    }

    // Xử lý các lỗi 403 khác
    if (
      error.response &&
      error.response.status === 403 &&
      error.response.data?.message !== "Invalid or expired token"
    ) {
      console.log("🚫 Forbidden (403) - Permission denied");
    }

    return Promise.reject(error);
  },
);

export const setSessionExpiredHandler = (handler) => {
  showSessionExpiredModal = handler;
};

export default https;
